// Re-syncs the bundle's per-variant contents (buybox list + "what's in the box") on variant change via
// the Section Rendering API, since Hyper doesn't re-render those. Scoped to the changed product's section
// (so quick-view / multiple products don't cross-target) and re-asserted via a MutationObserver rather than
// a fixed timeout, because Hyper's own re-render can land late and would otherwise revert our swap.
(() => {
  const BUYBOX = '.bundle-summary';
  const GRID = '.section-bundle-whats-in-box';

  const sectionIdOf = (element) => {
    const wrapper = element.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
  };

  const applySwap = (data, buybox, grid) => {
    const targets = [];
    if (buybox) targets.push({ sectionId: sectionIdOf(buybox), block: BUYBOX, element: buybox });
    if (grid) targets.push({ sectionId: sectionIdOf(grid), block: null, element: grid });

    targets.forEach((target) => {
      const rendered = data[target.sectionId];
      if (!rendered) return;
      const parsed = new DOMParser().parseFromString(rendered, 'text/html');
      if (target.block) {
        // Buybox is a block inside the main product section: lift that block out and swap it.
        const fresh = parsed.querySelector(target.block);
        if (fresh && target.element) target.element.replaceWith(fresh);
      } else {
        // Standalone section: replace the whole wrapper with the freshly rendered one.
        const current = document.getElementById('shopify-section-' + target.sectionId);
        if (!current) return;
        const fresh = parsed.getElementById('shopify-section-' + target.sectionId);
        if (fresh) current.replaceWith(fresh);
        else current.replaceChildren(...parsed.body.childNodes);
      }
    });
  };

  let inFlight;
  let reassertObserver;
  let reassertCeiling;

  // Hyper re-renders the buybox/grid shortly after a variant change and would revert our swap. Watch the
  // stable section ancestors (so a full-wrapper replace can't detach us) and re-apply until it settles;
  // device-speed agnostic, unlike a fixed timeout a slow render can outlast. A self-mutation guard stops
  // our own re-apply re-triggering the observer, and a ceiling disconnects once Hyper has settled.
  const watchAndReassert = (data, buybox, grid) => {
    if (reassertObserver) reassertObserver.disconnect();
    clearTimeout(reassertCeiling);

    const observeTargets = new Set();
    if (buybox) {
      const wrapper = buybox.closest('[id^="shopify-section-"]');
      if (wrapper) observeTargets.add(wrapper);
    }
    if (grid) {
      const wrapper = grid.closest('[id^="shopify-section-"]');
      if (wrapper && wrapper.parentNode) observeTargets.add(wrapper.parentNode);
    }
    if (!observeTargets.size) return;

    let selfMutating = false;
    reassertObserver = new MutationObserver(() => {
      if (selfMutating) return;
      selfMutating = true;
      applySwap(data, buybox, grid);
      requestAnimationFrame(() => {
        selfMutating = false;
      });
    });
    observeTargets.forEach((target) => reassertObserver.observe(target, { childList: true, subtree: true }));
    reassertCeiling = setTimeout(() => reassertObserver && reassertObserver.disconnect(), 3000);
  };

  const refresh = async (variantId, target) => {
    if (!variantId) return;

    const sectionWrapper = target ? target.closest('[id^="shopify-section-"]') : null;
    const buybox = sectionWrapper ? sectionWrapper.querySelector(BUYBOX) : document.querySelector(BUYBOX);
    const grid = document.querySelector(GRID);
    const sectionIds = [...new Set([buybox, grid].filter(Boolean).map(sectionIdOf).filter(Boolean))];
    if (!sectionIds.length) return;

    if (inFlight) inFlight.abort();
    const controller = new AbortController();
    inFlight = controller;

    const productPath = (target && target.dataset.url) || window.location.pathname;

    let data;
    try {
      const response = await fetch(`${productPath}?variant=${variantId}&sections=${sectionIds.join(',')}`, {
        signal: controller.signal,
      });
      if (!response.ok) return;
      data = await response.json();
    } catch (error) {
      return; // aborted or offline: leave the current contents in place
    }

    applySwap(data, buybox, grid);
    watchAndReassert(data, buybox, grid);
  };

  const onVariantChange = (variant, target) => {
    if (variant && variant.id) refresh(variant.id, target);
  };

  const pubsub = window.FoxTheme && FoxTheme.pubsub;
  if (pubsub && typeof pubsub.subscribe === 'function' && pubsub.PUB_SUB_EVENTS) {
    pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, (payload) => {
      onVariantChange(payload && payload.data && payload.data.variant);
    });
  }
  // Belt-and-braces: also catch the document event (fires alongside the pubsub), carrying its target
  // so the fetch resolves the right product path in quick-view / multi-product contexts.
  document.addEventListener('variant:changed', (event) => {
    onVariantChange(event.detail && event.detail.variant, event.target);
  });
})();
